import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { FixSafariCalendarIcon } from '@app/helpers/helpers';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { GlobalState } from '@app/store/store';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { LocalStorageService } from 'ngx-webstorage';
import { Subscription, tap } from 'rxjs';

@Component({
  selector: 'app-virtual-token-form',
  templateUrl: './virtual-token-form.component.html',
  styleUrls: ['./virtual-token-form.component.scss'],
  standalone: false,
})
export class VirtualTokenFormComponent implements OnInit, AfterViewInit {
  @Output() closeModalEvent = new EventEmitter<void>();

  constructor(
    private certificateService: CertificateService,
    private otpService: OtpService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private store: Store<GlobalState>,
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  loading: boolean = false;
  unlimitedSigns: boolean = false;
  isTrissUser: boolean = false; //triss user

  showStep1: any = true;
  showStep2: any = false;
  step1Attempted = false;
  step2Attempted = false;
  private validationAttemptSubscriptions: Subscription[] = [];
  /** Limite minimo selezionabile per la data di scadenza (oggi). */
  dateMinBound: Date | null = null;

  ngOnInit(): void {
    FixSafariCalendarIcon();
    this.dateMinBound = moment().startOf('day').toDate();

    this.store
      .pipe(
        tap(store => {
          if (store.session) {
            this.isTrissUser = store.session?.trissUser;
            console.log('triss: ' + this.isTrissUser);
          }
        })
      )
      .subscribe();

    this.setupValidationAttemptReset();
    this.updateUnlimitedAvailability();
  }

  ngAfterViewInit(): void {
    this.applyExpirationDateMinToCalendarInput();
  }

  private setupValidationAttemptReset(): void {
    this.validationAttemptSubscriptions.forEach(sub => sub.unsubscribe());
    this.validationAttemptSubscriptions = [];

    this.validationAttemptSubscriptions.push(
      this.name.valueChanges.subscribe(() => (this.step1Attempted = false))
    );
    this.validationAttemptSubscriptions.push(
      this.description.valueChanges.subscribe(() => (this.step1Attempted = false))
    );
    this.validationAttemptSubscriptions.push(
      this.f['signatures'].valueChanges.subscribe(() => {
        this.step1Attempted = false;
        this.updateUnlimitedAvailability();
      })
    );
    this.validationAttemptSubscriptions.push(
      this.expirationDate.valueChanges.subscribe(value => {
        this.step1Attempted = false;
        this.clampExpirationDateToBounds(value);
      })
    );
    this.validationAttemptSubscriptions.push(
      this.otp.valueChanges.subscribe(() => (this.step2Attempted = false))
    );
    this.validationAttemptSubscriptions.push(
      this.trissName.valueChanges.subscribe(() => (this.step1Attempted = false))
    );
    this.validationAttemptSubscriptions.push(
      this.trissDescription.valueChanges.subscribe(() => (this.step1Attempted = false))
    );
    this.validationAttemptSubscriptions.push(
      this.trissOtp.valueChanges.subscribe(() => (this.step2Attempted = false))
    );
  }

  description = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(3),
  ]);

  expirationDate = new UntypedFormControl('', [
    Validators.required,
    this.expirationDateNotInPastValidator,
  ]);
  name = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
  ]);

  otp = new UntypedFormControl('', [
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);
  unlimited = new UntypedFormControl(false, [Validators.requiredTrue]);

  form = new UntypedFormGroup({
    description: this.description,
    expirationDate: this.expirationDate,
    name: this.name,
    signatures: new UntypedFormControl({ value: '', disabled: false }, [
      Validators.required,
      Validators.pattern(/^[0-9]+$/),
    ]),
    otp: this.otp,
    unlimited: this.unlimited,
  });

  //triss flow

  trissDescription = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(3),
  ]);

  trissName = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(8),
  ]);

  trissOtp = new UntypedFormControl('', [
    Validators.minLength(6),
    Validators.maxLength(8),
  ]);

  trissForm = new UntypedFormGroup({
    trissName: this.trissName,
    trissDescription: this.trissDescription,
    trissOtp: this.trissOtp,
  });

  // convenience getter for easy access to form fields
  get f() {
    return this.form.controls;
  }

  get formErrors() {
    const errors = this.form.errors;
    return errors;
  }

  // convenience getter for easy access to form fields triss
  get trissF() {
    return this.trissForm.controls;
  }

  get trissFErrors() {
    const errors = this.trissForm.errors;
    return errors;
  }

  onSubmit() {
    this.loading = true;
    const currentForm = this.isTrissUser ? this.trissForm : this.form;
    if (currentForm.invalid) {
      this.step2Attempted = true;
      currentForm.markAllAsTouched();
      this.loading = false;
      return;
    }

    if (this.isTrissUser) {
      //triss
      this.certificateService
        //@ts-ignore
        .trissGenerateVirtualToken(
          this.trissF['trissDescription'].value.trim(),
          this.trissF['trissName'].value.trim(),
          this.trissF['trissOtp'].value.trim()
        )
        .subscribe({
          next: async response => {
            this.loading = false;

            const needsTokenSuccess = this.trissF['trissName'].value;
            this.localStorageService.store(
              'needsTokenSuccess',
              needsTokenSuccess
            );

            // Se tutto va bene
            this.closeModal();
            this.router
              .navigateByUrl('/', { skipLocationChange: true })
              .then(() => this.router.navigate(['otp/tokens']));
          },
          error: error => {
            console.error(error);
            this.goToStepOne();
            this.loading = false;
          },
          complete: () => {
            this.loading = false;
          },
        });
    } else {
      if (this.unlimitedSigns) {
        this.f['signatures'].setValue('UNLI');
      }
      this.certificateService
        //@ts-ignore
        .generateVirtualToken(
          this.f['description'].value.trim(),
          (this.f['expirationDate'].value + 'T00:00:00Z').trim(),
          this.f['name'].value.trim(),
          this.f['signatures'].value.trim(),
          this.f['otp'].value.trim()
        )
        .subscribe({
          next: async response => {
            this.loading = false;

            const needsTokenSuccess = this.f['name'].value;
            this.localStorageService.store(
              'needsTokenSuccess',
              needsTokenSuccess
            );

            // Se tutto va bene
            this.closeModal();
            this.router
              .navigateByUrl('/', { skipLocationChange: true })
              .then(() => this.router.navigate(['otp/tokens']));
          },
          error: error => {
            console.error(error);
            this.goToStepOne();
            this.loading = false;
          },
          complete: () => {
            this.loading = false;
          },
        });
    }
  }

  clearForm() {
    this.step1Attempted = false;
    this.step2Attempted = false;
    this.description = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(3),
    ]);
    this.expirationDate = new UntypedFormControl('', [
      Validators.required,
      this.expirationDateNotInPastValidator,
    ]);
    this.name = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
    ]);
    this.otp = new UntypedFormControl('', [
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);
    this.unlimited = new UntypedFormControl(false, [Validators.requiredTrue]);

    this.form = new UntypedFormGroup({
      description: this.description,
      expirationDate: this.expirationDate,
      name: this.name,
      signatures: new UntypedFormControl(
        {
          value: '',
          disabled: false,
        },
        [Validators.required, Validators.pattern(/^[0-9]+$/)]
      ),
      otp: this.otp,
      unlimited: this.unlimited,
    });
    this.setupValidationAttemptReset();
    this.updateUnlimitedAvailability();
    this.applyExpirationDateMinToCalendarInput();
  }
  //triss clear
  trissClearForm() {
    this.step1Attempted = false;
    this.step2Attempted = false;
    this.trissDescription = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(3),
    ]);
    this.trissName = new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
    ]);
    this.trissOtp = new UntypedFormControl('', [
      Validators.minLength(6),
      Validators.maxLength(8),
    ]);

    this.trissForm = new UntypedFormGroup({
      trissDescription: this.trissDescription,
      trissName: this.trissName,
      trissOtp: this.trissOtp,
    });
    this.setupValidationAttemptReset();
  }

  setUnlimitedSigns(event?: Event) {
    const signaturesControl = this.f['signatures'];
    const signaturesValue = (signaturesControl?.value ?? '').toString().trim();
    if (signaturesValue.length > 0) {
      this.unlimitedSigns = false;
      this.unlimited.setValue(false, { emitEvent: false });
      this.unlimited.disable({ emitEvent: false });
      this.unlimited.updateValueAndValidity({ emitEvent: false });
      return;
    }

    const checked =
      (event?.target as HTMLInputElement | undefined)?.checked ??
      this.unlimited.value === true;

    this.unlimitedSigns = checked;
    if (checked) {
      signaturesControl.clearValidators();
      signaturesControl.setErrors(null);
      signaturesControl.setValue('', { emitEvent: false });
      signaturesControl.disable({ emitEvent: false });
    } else {
      signaturesControl.enable({ emitEvent: false });
      signaturesControl.setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]+$/),
      ]);
    }
    signaturesControl.updateValueAndValidity({ emitEvent: false });

    this.unlimited.setValue(checked, { emitEvent: false });
    this.unlimited.markAsTouched();
    this.unlimited.updateValueAndValidity();
  }

  onSignaturesInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement | null;
    if (!inputElement) {
      return;
    }

    const digitsOnlyValue = inputElement.value.replace(/\D+/g, '');
    if (inputElement.value !== digitsOnlyValue) {
      inputElement.value = digitsOnlyValue;
    }

    this.f['signatures'].setValue(digitsOnlyValue, { emitEvent: true });
  }

  private updateUnlimitedAvailability(): void {
    const signaturesControl = this.f['signatures'];
    const signaturesValue = (signaturesControl?.value ?? '').toString().trim();
    const hasSignaturesValue = signaturesValue.length > 0;

    if (hasSignaturesValue) {
      this.unlimitedSigns = false;
      this.unlimited.setValue(false, { emitEvent: false });
      this.unlimited.disable({ emitEvent: false });
    } else if (!this.unlimitedSigns) {
      this.unlimited.enable({ emitEvent: false });
    }

    this.unlimited.updateValueAndValidity({ emitEvent: false });
  }

  goToSecondStep() {
    const currentForm = this.isTrissUser ? this.trissForm : this.form;
    if (currentForm.invalid) {
      this.step1Attempted = true;
      currentForm.markAllAsTouched();
      return;
    }
    this.step1Attempted = false;

    this.sendOtp();
    if (this.isTrissUser) {
      const trissOtpControl = this.trissF['trissOtp'];
      trissOtpControl.addValidators([
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(8),
      ]);
      trissOtpControl.setValue('', { emitEvent: false });
      trissOtpControl.markAsPristine();
      trissOtpControl.markAsUntouched();
      trissOtpControl.updateValueAndValidity({ emitEvent: false });
    } else {
      const otpControl = this.f['otp'];
      otpControl.addValidators([
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(8),
      ]);
      otpControl.setValue('', { emitEvent: false });
      otpControl.markAsPristine();
      otpControl.markAsUntouched();
      otpControl.updateValueAndValidity({ emitEvent: false });
    }

    this.showStep1 = false;
    this.showStep2 = true;
  }

  public goToStepOne() {
    this.step2Attempted = false;
    const otpControl = this.f['otp'];
    otpControl.setValue('', { emitEvent: false });
    otpControl.markAsPristine();
    otpControl.markAsUntouched();
    const trissOtpControl = this.trissF['trissOtp'];
    trissOtpControl.setValue('', { emitEvent: false });
    trissOtpControl.markAsPristine();
    trissOtpControl.markAsUntouched();
    this.otpService.resetOtpInputVal(true);
    this.unlimitedSigns = false;
    if (this.isTrissUser) {
      this.trissClearForm();
    } else {
      this.clearForm();
    }
    this.showStep1 = true;
    this.showStep2 = false;
  }

  sendOtp() {
    this.otpService.generateCode().subscribe({
      next: async response => {
        // Se tutto va bene
      },
      error: error => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  closeModal() {
    this.goToStepOne();
    this.closeModalEvent.emit();
  }

  ngOnDestroy(): void {
    this.validationAttemptSubscriptions.forEach(sub => sub.unsubscribe());
    this.validationAttemptSubscriptions = [];
  }

  private expirationDateNotInPastValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = (control.value ?? '').toString().trim();
    if (!value) {
      return null;
    }

    const selectedDate = moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
    if (!selectedDate.isValid()) {
      return { invalidDate: true };
    }

    return selectedDate.isBefore(moment().startOf('day'))
      ? { minDate: true }
      : null;
  }

  get minDate(): string {
    return moment().format('YYYY-MM-DD');
  }

  private clampExpirationDateToBounds(value: unknown): void {
    const rawValue = (value ?? '').toString().trim();
    if (!rawValue || !this.dateMinBound) {
      return;
    }

    const selectedDate = moment(rawValue, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
    if (!selectedDate.isValid()) {
      return;
    }

    const minDate = moment(this.dateMinBound).startOf('day');
    if (selectedDate.isBefore(minDate, 'day')) {
      this.expirationDate.setValue(minDate.format('YYYY-MM-DD'), {
        emitEvent: false,
      });
      this.expirationDate.updateValueAndValidity({ emitEvent: false });
    }
  }

  private applyExpirationDateMinToCalendarInput(): void {
    const dateInput = this.elementRef.nativeElement.querySelector(
      '#expirationDate, input[name="expirationDate"]'
    ) as HTMLInputElement | null;

    if (!dateInput) {
      return;
    }

    this.renderer.setAttribute(dateInput, 'min', this.minDate);
  }
}
